import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TrackerTypesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrackerTypeActions from "../../../actions/trackerTypes";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./TrackerTypesPage.scss";

interface TrackerTypesPageProps {
    formFilter: IFormFilter;
    trackerTypes: Dtos.TrackerType[];
    loadingTrackerTypes: boolean;
    loadTrackerTypesSuccess: boolean;
    loadTrackerTypesFailure: boolean;
    loadTrackerTypesErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearTrackerTypes: TrackerTypeActions.ITrackerTypeClearActionCreator;
    loadTrackerTypes: TrackerTypeActions.ITrackerTypeLoadActionCreator;
    filter: TrackerTypeActions.ITrackerTypeFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const TrackerTypesPage: FC<TrackerTypesPageProps> = ({
    formFilter,
    trackerTypes,
    loadingTrackerTypes,
    loadTrackerTypesSuccess,
    loadTrackerTypesFailure,
    loadTrackerTypesErrorCode,
    permissions,
    pageSize,
    clearTrackerTypes,
    loadTrackerTypes,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showTrackerTypes = !loadingTrackerTypes && loadTrackerTypesSuccess;


    // Load tracker types
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(TrackerTypesBreadcrumbs());

            loadTrackerTypes();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        TrackerTypesBreadcrumbs,
        loadTrackerTypes,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrackerTypesFailure) {
            navigate(`/error/${loadTrackerTypesErrorCode}`);
        }
    }, [loadTrackerTypesFailure, loadTrackerTypesErrorCode, navigate]);

    // Cleanup effect to clear the tracker types on component unmount
    useEffect(() => {
        return () => {
            clearTrackerTypes();
        };
    }, [clearTrackerTypes]);

    const createTable = useMemo(() => {
        if (!trackerTypes || trackerTypes.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no tracker types to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = trackerTypes.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trackerTypes}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : trackerTypes.length
                    }
                    pageSize={showPagination ? pageSize : trackerTypes.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no tracker types to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/settings/qa/trackertypes/" +
                                    rowInfo.original.id;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        trackerTypes,
        PAGE_SIZE_OPTIONS,
        pageSize,
        DEFAULT_PAGE_SIZE,
        navigate,
        setPageSize,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Tracker Types</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TaskTypesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/qa/trackertypes/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrackerTypes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrackerTypes ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showTrackerTypes, createTable]);

    return (
        <QALookupsLayout
            loading={loadingTrackerTypes}
            permission={Dtos.Permission.TaskTypesView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrackerTypesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.trackerTypes.formFilter,
        trackerTypes: state.trackerTypes.formFilteredData,
        loadingTrackerTypes:
            state.trackerTypes.loadState &&
            state.trackerTypes.loadState.status == RequestState.Pending,
        loadTrackerTypesSuccess:
            state.trackerTypes.loadState &&
            state.trackerTypes.loadState.status == RequestState.Success,
        loadTrackerTypesFailure:
            state.trackerTypes.loadState &&
            state.trackerTypes.loadState.status == RequestState.Failure,
        loadTrackerTypesErrorCode: state.trackerTypes.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrackerTypesPageProps> => {
    return {
        loadTrackerTypes: bindActionCreators(
            TrackerTypeActions.LoadTrackerType,
            dispatch
        ),
        clearTrackerTypes: bindActionCreators(
            TrackerTypeActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(TrackerTypeActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrackerTypesPage);
